import { z } from 'zod';
import { uuidSchema } from './common';
import type { ModuleKey } from './modules';
import type { Permission } from './permissions';

/**
 * The kinds of thing this platform holds, and what may be done with one.
 *
 * This catalog began life inside `links.ts` as "the kinds that can take part in
 * a cross-reference", and `docs/meetings-design.md` section 10.1 argued it
 * should become the platform's own registry of its kinds. Four mechanisms now
 * read it and only one of them is about linking:
 *
 *  - cross-references, which is where it started (`links.ts`);
 *  - meeting agenda references, which address items without going near a link;
 *  - revisions, comments and votes, which are keyed by `(entityKind, entityId)`
 *    and authorize from the `readPermission` declared here
 *    (`docs/register-collaboration-design.md` section 7.2);
 *  - date markers, which ask each kind for the dates it wants drawn on a
 *    timeline.
 *
 * So it lives here, and `links.ts` keeps only what is genuinely about links.
 * The old `LINK_ENTITY_*` spellings remain exported from there as deprecated
 * aliases, so the move is a file move rather than a rename of 121 call sites.
 */

/* --------------------------------------------------------------- the kinds */

/**
 * Item kinds the platform can address. Closed, because each one needs a
 * provider on the API side that can resolve it to a name and an href, and a
 * delete trigger that keeps the rows naming it from outliving it.
 *
 * `project` and `user` are deliberately absent. A link to the project every
 * item is already inside says nothing, and a person is not a concept being
 * cross-referenced -- ownership and participation are modelled where they
 * belong. Both are one member away if that turns out to be wrong.
 */
export const ENTITY_KINDS = [
  'schedule_task',
  'map_feature',
  'infrastructure_asset',
  'cost_account',
  /*
   * The register (docs/meetings-design.md section 4). Five kinds over one
   * table, listed separately here rather than as one `register_item` member
   * with a subtype, because this list is what the picker offers and what the
   * linked-items panel groups by: "register item" is not a thing anybody is
   * looking for, and a group mixing three risks with a decision reads as a
   * filing error.
   */
  'risk',
  'opportunity',
  'idea',
  'decision',
  'issue',
  'observation',
  /*
   * The document register (docs/document-register-design.md section 4). A
   * drawing is one of the most cross-referenced things on a project -- the
   * task that builds from it, the feature it covers, the decision that
   * commissioned it -- and the register shipped without any of that because it
   * was built as an island.
   *
   * The *document* is the kind, never the revision. "Rev B" has no identity
   * anybody quotes without the document it belongs to, and a comment thread
   * per issue would scatter one conversation across five rows.
   */
  'document',
  /*
   * The fleet (docs/clean-vehicles-design.md section 9). Both kinds are
   * organization-scoped: a framework agreement for waste collection is not part
   * of an infrastructure project, and neither is the lorry that serves it.
   */
  'vehicle',
  'framework_agreement',
] as const;
export const entityKindSchema = z.enum(ENTITY_KINDS);
export type EntityKind = (typeof ENTITY_KINDS)[number];

/**
 * Whether people may talk about a kind, and whether they may vote on one
 * (docs/register-collaboration-design.md section 6.6).
 *
 * Declared per kind rather than granted to everything, because neither belongs
 * everywhere: commenting on a service discovered from monitoring is talking to
 * a row nobody authored, and voting on whether a risk exists is not a thing
 * anybody wants to do. The API refuses a vote on a kind that does not declare
 * one, rather than accepting and ignoring it -- a value silently discarded is a
 * value its author believes was saved.
 */
export interface EntityCollaboration {
  comments: boolean;
  voting: boolean;
  /**
   * Whether somebody may ask to be told when one of these changes
   * (docs/document-register-design.md section 5.2).
   *
   * Declared per kind for a sharper reason than the other two. Following is
   * only half a mechanism: the subscription is generic, but the *sending* is
   * written by the module that knows which of its events are worth an email.
   * A follow control on a kind whose module emits nothing is a button that
   * silently promises something the product never delivers -- worse than a
   * missing button, because the person who pressed it stops watching the
   * screen.
   *
   * So a kind declares this when, and only when, it sends. Exactly one does
   * today; the register and the schedule flip the flag on the day they grow
   * their own notifications.
   */
  following: boolean;
}

export interface EntityDefinition {
  kind: EntityKind;
  /** Switched off with its module, without the consuming service learning what
   *  a module is -- see `readableKindsFor`. */
  module: ModuleKey;
  /** Held by anyone allowed to know this item exists. Gates whether a link to
   *  it appears in somebody's list at all, and whether its history and its
   *  comments can be read. */
  readPermission: Permission;
  /** Held by anyone allowed to edit this item. One end's manage permission is
   *  enough to create or remove a link; see `canWriteLink`.
   *
   *  Deliberately *not* what commenting requires: see
   *  `docs/register-collaboration-design.md` section 6.3. */
  managePermission: Permission;
  /**
   * Whether an item of this kind belongs to exactly one project. Two
   * project-scoped items may only be linked inside the same project; an
   * organization-scoped one -- shared infrastructure with no project -- may be
   * linked from anywhere.
   */
  scope: 'project' | 'organization';
  /** Translation key for the kind's name, used as the group heading in the
   *  linked-items list and as the label in the kind picker. */
  labelKey: string;
  /** What may be said about one of these. */
  collaboration: EntityCollaboration;
  /** Order the kinds are grouped in. Stable, so a list does not reshuffle when
   *  a link is added. */
  order: number;
}

export const ENTITY_CATALOG = {
  schedule_task: {
    kind: 'schedule_task',
    module: 'scheduling',
    readPermission: 'schedule.read',
    managePermission: 'schedule.manage',
    scope: 'project',
    labelKey: 'links.kind.schedule_task',
    /* Comments, because "why did this slip?" is asked about a task constantly
     * and the answer currently lives in somebody's mail. No voting: when a
     * task happens is a planning decision, not a popularity one. */
    collaboration: { comments: true, voting: false, following: false },
    order: 10,
  },
  map_feature: {
    kind: 'map_feature',
    module: 'spatial',
    readPermission: 'map.read',
    managePermission: 'map.manage',
    scope: 'project',
    labelKey: 'links.kind.map_feature',
    collaboration: { comments: true, voting: false, following: false },
    order: 20,
  },
  infrastructure_asset: {
    kind: 'infrastructure_asset',
    module: 'infrastructure',
    readPermission: 'infrastructure.read',
    managePermission: 'infrastructure.manage',
    /**
     * Organization-scoped, unlike the other three. `service.projectId` is
     * nullable because a monitoring host or a provider endpoint belongs to the
     * installation rather than to one project, and refusing to link those would
     * make the common case the unsupported one.
     */
    scope: 'organization',
    labelKey: 'links.kind.infrastructure_asset',
    /* Neither. A service is discovered from monitoring rather than authored --
     * the same reason it has no creator in the surface registry -- and a
     * discussion thread on a row nobody wrote has no author to answer it. */
    collaboration: { comments: false, voting: false, following: false },
    order: 30,
  },
  cost_account: {
    kind: 'cost_account',
    module: 'costing',
    readPermission: 'cost.read',
    managePermission: 'cost.manage',
    scope: 'project',
    labelKey: 'links.kind.cost_account',
    collaboration: { comments: true, voting: false, following: false },
    order: 40,
  },

  /*
   * The register. Five entries, one table, one module and one pair of
   * permissions -- so the split into five kinds costs five rows here and
   * nothing anywhere else. Their order interleaves them by how often they are
   * cross-referenced rather than alphabetically: a decision is the register
   * entry most often attached to something, and an idea the least.
   */
  decision: {
    kind: 'decision',
    module: 'register',
    readPermission: 'register.read',
    managePermission: 'register.manage',
    scope: 'project',
    labelKey: 'links.kind.decision',
    collaboration: { comments: true, voting: false, following: false },
    order: 50,
  },
  risk: {
    kind: 'risk',
    module: 'register',
    readPermission: 'register.read',
    managePermission: 'register.manage',
    scope: 'project',
    labelKey: 'links.kind.risk',
    collaboration: { comments: true, voting: false, following: false },
    order: 60,
  },
  issue: {
    kind: 'issue',
    module: 'register',
    readPermission: 'register.read',
    managePermission: 'register.manage',
    scope: 'project',
    labelKey: 'links.kind.issue',
    collaboration: { comments: true, voting: false, following: false },
    order: 70,
  },
  observation: {
    kind: 'observation',
    module: 'register',
    readPermission: 'register.read',
    managePermission: 'register.manage',
    scope: 'project',
    labelKey: 'links.kind.observation',
    /* Comments, no voting -- the same answer the other five give, and for a
       sharper reason here: an observation records what somebody saw, and a
       register that let people vote on whether they saw it is a register that
       stops being filled in. */
    collaboration: { comments: true, voting: false, following: false },
    order: 75,
  },
  opportunity: {
    kind: 'opportunity',
    module: 'register',
    readPermission: 'register.read',
    managePermission: 'register.manage',
    scope: 'project',
    labelKey: 'links.kind.opportunity',
    /*
     * One of the two kinds that can be voted on. An opportunity and an idea are
     * the register's two proposals -- things somebody thinks are worth doing
     * and nobody has committed to yet -- and "how many of us think so" is
     * exactly the question a list of forty of them cannot otherwise answer.
     */
    collaboration: { comments: true, voting: true, following: false },
    order: 80,
  },
  idea: {
    kind: 'idea',
    module: 'register',
    readPermission: 'register.read',
    managePermission: 'register.manage',
    scope: 'project',
    labelKey: 'links.kind.idea',
    collaboration: { comments: true, voting: true, following: false },
    order: 90,
  },

  document: {
    kind: 'document',
    module: 'documents',
    readPermission: 'document.read',
    managePermission: 'document.manage',
    scope: 'project',
    labelKey: 'links.kind.document',
    /*
     * Comments, because the thing people say about a drawing -- "this
     * conflicts with the signalling layout at ch. 4+200" -- is said today in an
     * email nobody else on the project can find, and it belongs against the
     * revision everybody is looking at.
     *
     * No voting. Which revision is current is a fact somebody issued, not a
     * tally, and the register exists precisely so that it is not up for a show
     * of hands.
     *
     * Following, and this is the only kind that declares it: the document
     * register is the one module that sends when its items change. See
     * `EntityCollaboration.following`.
     */
    collaboration: { comments: true, voting: false, following: true },
    /* Between the cost accounts and the register's own kinds. A drawing is
       referenced about as often as a decision and rather more often than an
       idea, and the order is what the picker and the linked-items panel group
       by. */
    order: 45,
  },
  vehicle: {
    kind: 'vehicle',
    module: 'fleet',
    readPermission: 'fleet.read',
    managePermission: 'fleet.manage',
    scope: 'organization',
    labelKey: 'links.kind.vehicle',
    /* Comments, because "this one was off the road all March" is said about a
     * vehicle constantly and currently lives in somebody's mail. No voting:
     * whether a lorry ran on a contract is a fact, not a tally. No following
     * yet -- the fleet module sends about agreements, not about vehicles, and
     * a follow control on a kind whose module never sends is a button that
     * silently promises nothing. */
    collaboration: { comments: true, voting: false, following: false },
    /* After every project kind. The fleet is organization-scoped, so a picker
       showing both puts the things that belong to the project first. */
    order: 100,
  },
  framework_agreement: {
    kind: 'framework_agreement',
    module: 'fleet',
    readPermission: 'fleet.agreement.read',
    managePermission: 'fleet.agreement.manage',
    scope: 'organization',
    labelKey: 'links.kind.framework_agreement',
    /* Comments, because an agreement is what a procurement officer and a
     * supplier argue about, and that argument currently lives in email.
     *
     * Following is deliberately *false*, and this is the one place worth
     * explaining why it is not true yet. The flag is a promise that the module
     * sends -- an agreement ending, its option running out -- and the sending
     * code is not built. `subscriptions.test.ts` is what holds that promise to
     * account, and it is right to. The flag flips in the same commit as the
     * notification, not before it. */
    collaboration: { comments: true, voting: false, following: false },
    order: 110,
  },
} as const satisfies Record<EntityKind, EntityDefinition>;

export const ENTITY_DEFINITIONS: readonly EntityDefinition[] = Object.values(ENTITY_CATALOG).sort(
  (a, b) => a.order - b.order,
);

export function entityDefinition(kind: EntityKind): EntityDefinition {
  return ENTITY_CATALOG[kind];
}

export function isEntityKind(value: string): value is EntityKind {
  return (ENTITY_KINDS as readonly string[]).includes(value);
}

/**
 * The kinds a caller may see at all.
 *
 * Module enablement needs no separate check: `session.permissions` is already
 * the intersection of what the user's roles grant with what the deployment's
 * modules govern (`modules.ts`), so a kind whose module is off has an
 * unholdable read permission and drops out here.
 *
 * This one function is the authorization story for the link picker, the agenda
 * builder and the marker query alike -- which is why switching the register
 * module off takes its deadlines off the Gantt with no code in scheduling
 * knowing that markers or modules exist.
 */
export function readableKindsFor(permissions: readonly Permission[]): EntityKind[] {
  const held = new Set(permissions);
  return ENTITY_DEFINITIONS.filter((definition) => held.has(definition.readPermission)).map(
    (definition) => definition.kind,
  );
}

/** Whether a kind takes comments at all. Checked on write; the panel reads the
 *  same flag to decide whether to render. */
export function allowsComments(kind: EntityKind): boolean {
  return ENTITY_CATALOG[kind].collaboration.comments;
}

/** Whether a kind takes votes. See `EntityCollaboration`. */
export function allowsVoting(kind: EntityKind): boolean {
  return ENTITY_CATALOG[kind].collaboration.voting;
}

/** Whether a kind can be followed -- which is to say, whether its module sends
 *  anything. See `EntityCollaboration.following`. */
export function allowsFollowing(kind: EntityKind): boolean {
  return ENTITY_CATALOG[kind].collaboration.following;
}

/* ------------------------------------------------------- entity references */

/**
 * The address of one item: what kind it is, and which one.
 *
 * A UUID alone would be ambiguous -- the same identifier space is used by every
 * table -- and, more to the point, unresolvable: nothing could look it up
 * without knowing where to look.
 */
export const entityRefSchema = z.object({ kind: entityKindSchema, id: uuidSchema }).strict();
export type EntityRef = z.infer<typeof entityRefSchema>;

/** The sortable form of a reference. Also the storage ordering key -- see
 *  `canonicalizeLink`. */
export function entityRefKey(ref: EntityRef): string {
  return `${ref.kind}:${ref.id}`;
}

export function sameEntityRef(a: EntityRef, b: EntityRef): boolean {
  return a.kind === b.kind && a.id === b.id;
}

/**
 * An anchor as a query pair: `?kind=decision&id=...`.
 *
 * A pair rather than two path segments, following the reasoning
 * `LinksController` already states for the same address: `kind` and `id` are
 * one address in two parts, and `/entities/decision/:id/comments` would read as
 * a collection of decisions when it is nothing of the sort.
 */
export const entityAnchorQuerySchema = z.object({ kind: entityKindSchema, id: uuidSchema });
export type EntityAnchorQuery = z.infer<typeof entityAnchorQuerySchema>;

/** The unordered pair, as one string. Two links between the same two items
 *  produce the same value whichever way round they were authored, which is what
 *  the read path dedupes on. */
export function entityPairKey(a: EntityRef, b: EntityRef): string {
  const [low, high] = [entityRefKey(a), entityRefKey(b)].sort();
  return `${low}|${high}`;
}
